import React from 'react';
import {Plugins, TypeImplementation, withSystem} from '@packages/systems/wfdl';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {TRecord, TTypeConstructor, EBoolean} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {Subheading} from '@packages/systems/design-system/Text';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {
  type ContextualExpression,
  EBoolean as aEBoolean,
  ContextualExpressions,
  stripAnn,
  updateExpr,
} from '@packages/utilities/contextual-expression';
import {
  START_AT_PATH,
  SHOW_ALL_RELATED_VIDEO_PATH,
  CONTROLS_PATH,
  AUTOPLAY_PATH,
  MUTED_PATH,
  PRIVACY_MODE_PATH,
} from '../constants';

const {getIn} = ContextualExpressions;

const type = TRecord({
  // URL-fetch options that can ride with meta in EXPRESSIONS_CHANGED.
  startAt: withSystem(
    {label: 'Start at', isLocalizable: true},
    TTypeConstructor(qualify(Plugins.name('Basic'), typeName('TimeNumber')))
  ),
  showAllRelatedVideos: withSystem(
    {
      label: 'Limit related videos to channel',
      defaultValue: true,
      labelVariant: 'hidden',
      isLocalizable: true,
    },
    TTypeConstructor(
      qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
    )
  ),
  controls: withSystem(
    {
      label: 'Show player controls',
      defaultValue: true,
      labelVariant: 'hidden',
      isLocalizable: true,
    },
    TTypeConstructor(
      qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
    )
  ),
  autoplay: withSystem(
    {isLocalizable: true},
    TTypeConstructor(
      qualify(Plugins.name('Embed'), typeName('AutoplayTooltip'))
    )
  ),
  muted: withSystem(
    {label: 'Mute', labelVariant: 'hidden', isLocalizable: true},
    TTypeConstructor(
      qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
    )
  ),
  privacyMode: TTypeConstructor(
    qualify(Plugins.name('Embed'), typeName('PrivacyModeTooltip'))
  ),
});

const Divider = emotionStyled('div')({
  height: 1,
  backgroundColor: tokens.colors.background4,
  width: `100%`,
  margin: `${tokens.space[2]} 0`,
});

const YouTubeTypeRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const expressionEditorAtPath = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'path' implicitly has an 'any' type.
    (path) => {
      const exprAt = getIn(expr, path);
      if (!exprAt) {
        throw Error(
          `Failed to get expression at path: ${JSON.stringify(path)}`
        );
      }
      const dispatchAt = extendExpressionEditorActionAtPath(dispatch)(path);

      return <ExpressionEditor expression={exprAt} dispatch={dispatchAt} />;
    },
    [expr, dispatch]
  );

  const getShowAllRelatedVideosExpr = React.useCallback(() => {
    const exprAt = getIn(expr, SHOW_ALL_RELATED_VIDEO_PATH);
    if (!exprAt) {
      throw Error(
        `Failed to get expression at path: ${JSON.stringify(
          SHOW_ALL_RELATED_VIDEO_PATH
        )}`
      );
    }

    const exprValue = ContextualExpressions.getBoolean(stripAnn(exprAt));
    const newExpr = updateExpr((_) => aEBoolean(!exprValue))(exprAt);

    const dispatchAt = (action: any) => {
      extendExpressionEditorActionAtPath(dispatch)(SHOW_ALL_RELATED_VIDEO_PATH)(
        {
          ...action,
          payload: {
            ...action.payload,
            newExpression: EBoolean(!exprValue),
          },
        }
      );
    };

    return <ExpressionEditor expression={newExpr} dispatch={dispatchAt} />;
  }, [expr, dispatch]);

  const StartAt = expressionEditorAtPath(START_AT_PATH);
  const Mute = expressionEditorAtPath(MUTED_PATH);
  const Autoplay = expressionEditorAtPath(AUTOPLAY_PATH);
  const Controls = expressionEditorAtPath(CONTROLS_PATH);
  const PrivacyMode = expressionEditorAtPath(PRIVACY_MODE_PATH);
  const ShowAllRelatedVideos = getShowAllRelatedVideosExpr();

  return (
    <>
      {StartAt}
      <Divider />
      <Subheading>Playback</Subheading>
      {Mute}
      {Autoplay}
      {Controls}
      <Divider />
      <Subheading>Privacy</Subheading>
      {PrivacyMode}
      {ShowAllRelatedVideos}
    </>
  );
};

const YouTubeOptionsType = TypeImplementation.Alias({
  type,
  render: YouTubeTypeRender,
});

export default YouTubeOptionsType;
